import type { LinkingOptions } from '@react-navigation/native';
import type { RootStackNavigatorParamList } from '@south-street-app/navigation/types';

import React from 'react';

import { StyleSheet, Platform, LogBox } from 'react-native';

import appConfig from '@default-tamagui-config';
import { NavigationContainer } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import { images } from '@shared/assets';
import { setupSentry } from '@south-street-app/configs';
import { useE2EReadiness } from '@south-street-app/hooks';
import { toastConfig } from '@south-street-app/molecules';
import { RootStackNavigator } from '@south-street-app/navigation';
import { GlobalBottomSheet } from '@south-street-app/organisms';
import {
  NoConnectionProvider,
  QueryProvider,
  CheckJailbrokenProvider,
  CountdownProvider,
  LaunchDarklyProvider,
  MaintenanceModeProvider,
} from '@south-street-app/providers';
import { useCustomFonts, useFontsWebLoader } from '@south-street-app/theme';
import { TwilioWrapper } from '@south-street-app/utils';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import {
  configureReanimatedLogger,
  ReanimatedLogLevel,
} from 'react-native-reanimated';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import Toast from 'react-native-toast-message';
import { TamaguiProvider } from 'tamagui';

configureReanimatedLogger({
  level: ReanimatedLogLevel.warn,
  strict: false, // Disable strict mode warnings
});

// Suppress warnings from third-party packages - must be before other imports
LogBox.ignoreLogs([
  "[DEPRECATED] Default export is deprecated. Instead use `import { shallow } from 'zustand/shallow'`.",
  'zustand/shallow',
  '[zustand persist middleware] Unable to update item',
  'auth-storage',
  'useBottomSheetDynamicSnapPoints',
]);

const SplashView = Platform.select({
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  native: () => require('@utility-nyc/react-native-splash-view').SplashView,
  web: () => null,
});

const SplashViewComponent = SplashView ? SplashView() : null;

const styles = StyleSheet.create({
  gestureHandler: {
    flex: 1,
  },
});

const linking: LinkingOptions<RootStackNavigatorParamList> = {
  prefixes: ['sss://', 'southstreetsecurities://'],
  config: {
    screens: {
      PublicStackNavigator: {
        screens: {
          SetPasswordScreen: 'set-password/:signature/:code',
        },
      },
    },
  },
};

setupSentry();

const App = () => {
  const [fontsLoaded] = useCustomFonts();

  useFontsWebLoader();

  useE2EReadiness('southStreetAppReady', fontsLoaded);

  return (
    <Sentry.ErrorBoundary>
      {fontsLoaded ? (
        <TamaguiProvider defaultTheme={'light'} config={appConfig}>
          <QueryProvider>
            <GestureHandlerRootView style={styles.gestureHandler}>
              <NavigationContainer linking={linking}>
                <SafeAreaProvider>
                  <TwilioWrapper>
                    <CheckJailbrokenProvider>
                      <NoConnectionProvider>
                        <CountdownProvider>
                          <LaunchDarklyProvider>
                            <MaintenanceModeProvider>
                              <RootStackNavigator />
                              <GlobalBottomSheet />
                              <Toast config={toastConfig} />
                            </MaintenanceModeProvider>
                          </LaunchDarklyProvider>
                        </CountdownProvider>
                      </NoConnectionProvider>
                    </CheckJailbrokenProvider>
                  </TwilioWrapper>
                </SafeAreaProvider>
              </NavigationContainer>
            </GestureHandlerRootView>
          </QueryProvider>
        </TamaguiProvider>
      ) : null}
      {Platform.OS !== 'web' && (
        <SplashViewComponent
          launchImageUrl={images.Splash}
          fadeOutDuration={800}
          animationTimeMs={2000}
          launchScreenHideMs={500}
        />
      )}
    </Sentry.ErrorBoundary>
  );
};

export default App;
